import { classToPlain, Exclude, plainToClass, Type } from "@eman/class-transformer";
import format from "date-fns/format";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import { action, computed, isObservableArray, observable } from "mobx";

import { getEnumAttributes } from "@util/Enumerable";

export const NORMALIZED_DATE_FORMAT = "yyyy-MM-dd";

export const CUSTOM_DATE_FORMAT = "dd. MM. yyyy";

export default abstract class BaseModel implements models.IBase {
  static __tracableModelInstance = true;

  static parseDate = (value: string): Date | undefined => {
    const parsed = parse(value, NORMALIZED_DATE_FORMAT, new Date());

    if (isValid(parsed)) {
      return parsed;
    } else {
      return undefined;
    }
  };

  static formatDate = (value: Date | undefined | null, dateFormat?: string) => {
    if (value && isValid(value)) {
      return format(value, dateFormat ? dateFormat : NORMALIZED_DATE_FORMAT);
    } else if (value === null) {
      return null;
    } else {
      return undefined;
    }
  };

  static parseType<TType>(value: any, type: new () => TType) {
    if (value === null || value === undefined) {
      return new type();
    } else {
      return plainToClass(type, value);
    }
  }

  static formatType(value: BaseModel) {
    return classToPlain(value);
  }

  @Exclude()
  @observable
  __alreadyAssignedEnums = false;

  @Exclude({ toPlainOnly: true })
  @observable
  id?: number;

  @Exclude()
  @observable
  saved = false;

  @Exclude()
  @observable
  markedForDestroy = false;

  @Exclude()
  @observable
  errors = observable.map();

  @Exclude()
  @observable
  oldValues: { [key: string]: any } = {};

  @Exclude()
  @observable
  isDirty = observable.map();

  @Exclude({ toPlainOnly: true })
  @Type(() => Date)
  @observable
  created_at: Date;

  @Exclude({ toPlainOnly: true })
  @Type(() => Date)
  @observable
  updated_at: Date;

  /**
   * Return number of changes.
   *
   * @return number
   */
  @computed
  get changes(): number {
    let changes = 0;

    if (this.isDirty) {
      this.isDirty.forEach(value => {
        if (value === true) {
          changes += 1;
        }
      });
    }

    this.isDirtyRelations().forEach(element => {
      if (this[element]) {
        changes += this[element].changes;
      }
    });

    return changes;
  }

  /**
   * Error count.
   *
   * @return number
   */
  @computed
  get errorCount(): number {
    let errors = 0;

    if (this.errors) {
      this.errors.forEach(value => {
        if (value && ((value.length && value.length > 0) || value !== "")) {
          errors += 1;
        }
      });
    }

    this.isDirtyRelations().forEach(element => {
      if (this[element]) {
        errors += this[element].errorCount;
      }
    });

    return errors;
  }

  /**
   * Override to observe relations for dirty changes!
   */
  isDirtyRelations(): string[] {
    return [];
  }

  /**
   * Determine if record is new record.
   *
   * @returns Boolean
   * @memberof BaseModel
   */
  @computed get newRecord(): boolean {
    return this.id === undefined;
  }

  /**
   * Generate string version of ID (for filters).
   *
   * @readonly
   * @type {string}
   * @memberof BaseModel
   */
  @computed get stringId(): string | undefined {
    return this.id ? String(this.id) : undefined;
  }

  /**
   * Clear all errors.
   */
  @action.bound
  clearErrors() {
    this.errors.clear();
    this.isDirty.clear();

    this.isDirtyRelations().forEach(element => {
      if (this[element]) {
        this[element].clearErrors();
      }
    });
  }

  /**
   * Reset all enums on current instance.
   *
   * @memberof BaseModel
   */
  resetEnums() {
    const attributes = getEnumAttributes(this);

    if (!attributes) {
      return;
    }

    for (const target in attributes) {
      if (attributes.hasOwnProperty(target)) {
        this[target] = undefined;
      }
    }
  }

  /**
   * Set errors.
   * @param errors Errors
   */
  @action.bound
  setErrors(errors: models.IErrors, clear = true) {
    if (clear) {
      this.errors.clear();
    }

    for (const key in errors) {
      if (errors.hasOwnProperty(key)) {
        const value = errors[key];

        if (key.indexOf(".") > 0) {
          const [collection, ...subKeys] = key.split(".");
          const subkey = subKeys.length === 1 ? subKeys[0] : subKeys.join(".");

          if (collection.indexOf("[") > 0 && collection.indexOf("]") > 0) {
            // 1:N collection with errors array

            const collectionName = collection.substr(0, collection.indexOf("["));
            const index = parseInt(
              collection.substr(collection.indexOf("[") + 1, collection.indexOf("]") - collection.indexOf("[")),
              10
            );

            if (this[collectionName] && isObservableArray(this[collectionName]) && this[collectionName].length > index) {
              this[collectionName][index].setErrors({ [subkey]: value }, false);
            }
          } else if (this[collection] && this[collection][0]) {
            // 1:N collections with one error
            this[collection][0].setErrors({ [subkey]: value }, false);
          } else if (this[collection]) {
            // 1:1 collections
            this[collection].setErrors({ [subkey]: value }, false);
          } else {
            // if "collection" doesn't exist on Model, use "base" key
            const base = this.errors.get("base") || [];
            base.push(value);
            this.errors.set("base", base);
          }
        } else {
          // "base" and other attribute's error messages
          this.errors.set(key, value);
        }
      }
    }
  }

  /**
   * Test if error is set.
   * @param key Key
   * @return boolean
   */
  hasError(key: string): boolean {
    return this.errors.has(key);
  }
}
